import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, ArrowLeft, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
  const otpValue = watch('otp', '');
  
  // Get email from location state
  const email = location.state?.email || '';

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  // Auto-hide success message
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-hide error message
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  const onSubmit = async (data) => {
    console.log('=== OTP VERIFICATION STARTED ===');
    console.log('Email:', email);
    console.log('OTP entered:', data.otp);
    console.log('OTP length:', data.otp?.length);
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        email: email,
        otp: data.otp
      };
      console.log('Sending OTP verification payload:', payload);
      
      const response = await api.post('/auth/verify-otp/', payload);
      console.log('OTP verification response:', response.data);

      if (response.data.message) {
        setSuccess('Account verified successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Account created successfully! Please log in with your credentials.' 
            }
          });
        }, 2000);
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.data?.non_field_errors) {
        setError(error.response.data.non_field_errors[0]);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.data) {
        const firstError = Object.values(error.response.data)[0];
        if (Array.isArray(firstError)) {
          setError(firstError[0]);
        } else {
          setError(firstError);
        }
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/auth/resend-otp/', {
        email: email
      });

      if (response.data.message) {
        setSuccess('OTP resent successfully! Please check your email.');
        setTimeLeft(600);
        setCanResend(false);
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError(error.response?.data?.error || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue('otp', value);
  };

  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-slate-50/30 to-blue-50/20 relative overflow-hidden">
      {/* Professional White & Blue Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Elegant Blue Gradient Orbs */}
        <div className="absolute w-[600px] h-[600px] bg-gradient-to-r from-blue-200/20 via-indigo-100/15 to-slate-200/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-gradient-to-r from-indigo-200/15 to-blue-100/10 rounded-full blur-3xl animate-pulse animation-delay-2000" />
        <div className="absolute bottom-1/3 right-1/3 w-[550px] h-[550px] bg-gradient-to-r from-slate-300/12 to-blue-200/8 rounded-full blur-3xl animate-pulse animation-delay-4000" />
        
        {/* Professional Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.03'%3E%3Ccircle cx='40' cy='40' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '80px 80px'
          }} />
        </div>

        {/* Elegant Light Rays */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-blue-200/20 to-transparent" />
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-indigo-300/15 to-transparent" />
        <div className="absolute top-0 left-2/3 w-px h-full bg-gradient-to-b from-transparent via-slate-200/10 to-transparent" />
      </div>

      {/* Professional Navigation */}
      <nav className="relative z-10 px-6 py-4 flex justify-between items-center backdrop-blur-xl bg-white/80 border-b border-blue-100/50">
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group hover:scale-110 transition-all duration-300">
              <span className="text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300">ET</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">EducateTrade</h1>
              <p className="text-xs text-gray-600 font-medium">Professional LSE Trading Platform</p>
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center space-x-8">
          <a href="#features" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">Features</a>
          <a href="#about" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">About</a>
          <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">Contact</a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="w-full max-w-md">
            {/* Back Button */}
            <div className="mb-6">
              <Link
                to="/register"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-300 font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Registration
              </Link>
            </div>

            {/* OTP Verification Card */}
            <div className="relative">
              <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl border border-blue-100/50 p-8 hover:bg-white/90 transition-all duration-500 group">
                {/* Professional Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-200/20 via-indigo-100/15 to-slate-200/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                
                <div className="text-center mb-8">
                  <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-700 mb-6 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-3 tracking-tight">
                    Verify Your Email
                  </h2>
                  <p className="text-gray-600 text-lg font-light mb-2">
                    We've sent a 6-digit code to
                  </p>
                  <p className="text-blue-600 font-semibold text-lg">
                    {email}
                  </p>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-5">
                    <div className="relative group">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Enter Verification Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="000000"
                          className="w-full px-4 py-4 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-2xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium text-center text-2xl tracking-widest"
                          {...register('otp', {
                            required: 'Verification code is required',
                            minLength: {
                              value: 6,
                              message: 'Code must be 6 digits'
                            },
                            maxLength: {
                              value: 6,
                              message: 'Code must be 6 digits'
                            },
                            pattern: {
                              value: /^\d{6}$/,
                              message: 'Code must contain only numbers'
                            }
                          })}
                          onChange={handleOTPChange}
                          maxLength={6}
                        />
                      </div>
                      {errors.otp && (
                        <p className="mt-2 text-sm text-red-500 font-medium">{errors.otp.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Timer and Resend */}
                  <div className="text-center space-y-4">
                    {timeLeft > 0 ? (
                      <div className="text-gray-600">
                        <p className="text-sm">Code expires in</p>
                        <p className="text-2xl font-bold text-blue-600">{formatTime(timeLeft)}</p>
                      </div>
                    ) : (
                      <div className="text-gray-600">
                        <p className="text-sm text-red-500 mb-2">Code has expired</p>
                        <button
                          type="button"
                          onClick={handleResendOTP}
                          disabled={isResending}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isResending ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Resend Code
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-4 rounded-2xl bg-red-50 border border-red-200">
                      <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
                        <p className="text-red-700 font-medium">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Success Message */}
                  {success && (
                    <div className="p-4 rounded-2xl bg-green-50 border border-green-200">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                        <p className="text-green-700 font-medium">{success}</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || otpValue.length !== 6}
                    className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 hover:from-blue-700 hover:via-indigo-700 hover:to-slate-800 text-white font-bold text-lg rounded-2xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        <span>Verifying...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        <span>Verify Account</span>
                      </div>
                    )}
                  </button>
                </form>

                {/* Help Text */}
                <div className="text-center mt-6 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50">
                  <p className="text-gray-600 text-sm">
                    Didn't receive the code? Check your spam folder or{' '}
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={!canResend || isResending}
                      className="text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      resend it
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
